/**
 * src/views/ProfileView.tsx
 * Ultra-Premium Apple iOS 18 Profile & Settings View
 * Features: CK ID Identity, Sound & Haptic Controls, Theme Switcher,
 * Push Notifications, Statement Export, and Role Management.
 */

import React, { useState, useRef, useEffect } from 'react';
import { 
  User, 
  Shield, 
  Volume2, 
  VolumeX, 
  Vibrate, 
  Sun, 
  Moon, 
  Bell, 
  FileSpreadsheet, 
  Printer, 
  LogOut, 
  Users, 
  Tag, 
  ArrowUpRight, 
  ArrowDownLeft, 
  CheckCircle2, 
  ChevronRight,
  Sparkles,
  Smartphone,
  Camera,
  Upload,
  Trash2,
  Image as ImageIcon,
  X,
  Save,
  Copy,
  Check,
  CreditCard,
  KeyRound
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNotifications } from '../context/NotificationContext';
import { useToast } from '../context/ToastContext';
import { 
  isSoundEnabled, 
  setSoundEnabled, 
  isHapticsEnabled, 
  setHapticsEnabled, 
  haptic, 
  playTapSound, 
  playSuccessSound 
} from '../utils/audioHaptics';
import { api, type TransactionItem } from '../services/api';
import { AppleCardStatementModal } from '../components/statement/AppleCardStatementModal';
import { ChangePasswordModal } from '../components/users/ChangePasswordModal';

// Apple 3D Memoji presets with vibrant gradient squircles
const MEMOJI_PRESETS = [
  { id: 'fox', emoji: '🦊', grad: ['#ff9500', '#ff3b30'], label: 'Fox' },
  { id: 'lion', emoji: '🦁', grad: ['#ffcc00', '#ff9500'], label: 'Lion' },
  { id: 'tiger', emoji: '🐯', grad: ['#ff9500', '#af52de'], label: 'Tiger' },
  { id: 'panda', emoji: '🐼', grad: ['#8e8e93', '#1c1c1e'], label: 'Panda' },
  { id: 'rocket', emoji: '🚀', grad: ['#007aff', '#5856d6'], label: 'Rocket' },
  { id: 'gem', emoji: '💎', grad: ['#5ac8fa', '#007aff'], label: 'Diamond' },
  { id: 'crown', emoji: '👑', grad: ['#af52de', '#ff2d55'], label: 'Royal' },
  { id: 'bolt', emoji: '⚡', grad: ['#ffd60a', '#ff9500'], label: 'Electric' },
  { id: 'cool', emoji: '🕶️', grad: ['#30d158', '#007aff'], label: 'Vision' },
  { id: 'boss', emoji: '💼', grad: ['#34c759', '#30b0c7'], label: 'Executive' }
];

const createEmojiAvatarUri = (emoji: string, grad: string[]) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120">
    <defs>
      <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="${grad[0]}" />
        <stop offset="100%" stop-color="${grad[1]}" />
      </linearGradient>
    </defs>
    <rect width="120" height="120" rx="36" fill="url(#bg)" />
    <text x="50%" y="54%" font-size="64" dominant-baseline="central" text-anchor="middle">${emoji}</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

interface ProfileViewProps {
  onNavigateTab: (tab: string) => void;
  onNewEntryClick?: () => void;
}

export const ProfileView: React.FC<ProfileViewProps> = ({
  onNavigateTab,
  onNewEntryClick
}) => {
  const { user, logout, refreshUser } = useAuth();
  const { theme, toggleTheme, setTheme, logoStyle, setLogoStyle, activeLogo } = useTheme();
  const { permission, requestPermission } = useNotifications();
  const { success, error: toastError, info } = useToast();

  const [soundActive, setSoundActive] = useState<boolean>(() => isSoundEnabled());
  const [hapticActive, setHapticActive] = useState<boolean>(() => isHapticsEnabled());
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isAvatarPickerOpen, setIsAvatarPickerOpen] = useState<boolean>(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState<boolean>(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState<boolean>(false);
  const [statementTransactions, setStatementTransactions] = useState<TransactionItem[]>([]);
  const [statementSummary, setStatementSummary] = useState({
    totalReceived: 0,
    totalPaid: 0,
    netDifference: 0
  });
  const [isLoadingStatement, setIsLoadingStatement] = useState<boolean>(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const isAdmin = user?.role === 'admin';

  const [upiInput, setUpiInput] = useState<string>(() => user?.upi_id || (user?.role === 'admin' ? 'chintankheradia123-1@okaxis' : ''));
  const [isSavingUpi, setIsSavingUpi] = useState<boolean>(false);
  const [copiedUpi, setCopiedUpi] = useState<boolean>(false);

  useEffect(() => {
    if (user) {
      setUpiInput(user.upi_id || (user.role === 'admin' ? 'chintankheradia123-1@okaxis' : ''));
    }
  }, [user]);

  const handleSaveUpi = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    haptic.button();
    playTapSound();
    setIsSavingUpi(true);
    try {
      const cleanUpi = upiInput.trim();
      await api.users.updateProfile({ upi_id: cleanUpi || null });
      await refreshUser();
      haptic.success();
      playSuccessSound();
      success(
        isAdmin
          ? `Admin settlement receiving UPI ID set to "${cleanUpi || 'chintankheradia123-1@okaxis'}"`
          : `Google Pay UPI ID saved: "${cleanUpi}". Admin can now settle payouts directly to your account.`,
        'UPI ID Saved'
      );
    } catch (err: any) {
      toastError(err.message || 'Failed to save UPI ID');
    } finally {
      setIsSavingUpi(false);
    }
  };

  const handleCopyUpi = () => {
    if (!upiInput.trim()) return;
    haptic.tap();
    playTapSound();
    navigator.clipboard.writeText(upiInput.trim());
    setCopiedUpi(true);
    success(`UPI ID copied: ${upiInput.trim()}`, 'Copied to Clipboard');
    setTimeout(() => setCopiedUpi(false), 2000);
  };

  // Toggle touch sounds
  const handleToggleSound = () => {
    const nextVal = !soundActive;
    setSoundActive(nextVal);
    setSoundEnabled(nextVal);
    if (nextVal) {
      setTimeout(() => {
        playSuccessSound();
      }, 50);
      success('Touch sounds enabled', 'Audio Active');
    } else {
      info('Touch sounds muted', 'Audio Silent');
    }
    haptic.tap();
  };

  // Toggle haptics
  const handleToggleHaptic = () => {
    const nextVal = !hapticActive;
    setHapticActive(nextVal);
    setHapticsEnabled(nextVal);
    if (nextVal) {
      haptic.success();
      success('Haptic feedback active', 'Tactile Engine');
    } else {
      info('Haptic feedback disabled', 'Haptics Off');
    }
    playTapSound();
  };

  // Download Statement CSV
  const handleExportCSV = async () => {
    haptic.tap();
    playTapSound();
    setIsExporting(true);
    try {
      const res = await api.transactions.list({ limit: 10000 });
      const items = res.transactions || [];

      if (items.length === 0) {
        toastError('No transactions available to export');
        setIsExporting(false);
        return;
      }

      // Generate UTF-8 BOM CSV
      const headers = ['ID', 'Date', 'Type', 'Amount (INR)', 'Category', 'User', 'Description', 'Created At'];
      const rows = items.map(t => [
        t.id,
        t.transaction_date,
        t.transaction_type.toUpperCase(),
        t.amount,
        `"${(t.category_name || '').replace(/"/g, '""')}"`,
        `"${(t.username || '').replace(/"/g, '""')}"`,
        `"${(t.description || '').replace(/"/g, '""')}"`,
        t.created_at
      ]);

      const csvContent = '\uFEFF' + [headers.join(','), ...rows.map(r => r.join(','))].join('\r\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CKAudit_Statement_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      haptic.success();
      playSuccessSound();
      success(`Exported ${items.length.toLocaleString('en-IN')} records successfully!`, 'Statement Generated');
    } catch (err: any) {
      toastError(err.message || 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  // Print Statement - Opens Apple Card Statement Modal with full ledger data
  const handlePrint = async () => {
    haptic.tap();
    playTapSound();
    setIsLoadingStatement(true);
    try {
      const res = await api.transactions.list({ limit: 10000 });
      const txs = res.transactions || [];
      setStatementTransactions(txs);
      if (res.summary && (res.summary.totalReceived > 0 || res.summary.totalPaid > 0)) {
        setStatementSummary(res.summary);
      } else {
        const paid = txs.filter(t => t.transaction_type === 'paid').reduce((acc, t) => acc + t.amount, 0);
        const received = txs.filter(t => t.transaction_type === 'receive').reduce((acc, t) => acc + t.amount, 0);
        setStatementSummary({
          totalReceived: received,
          totalPaid: paid,
          netDifference: received - paid
        });
      }
      setIsPrintModalOpen(true);
    } catch (err: any) {
      toastError(err.message || 'Failed to prepare statement for printing');
    } finally {
      setIsLoadingStatement(false);
    }
  };

  // Handle custom file upload with canvas compression
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toastError('Please select a valid image file');
      return;
    }

    setIsUploadingPhoto(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = async () => {
        try {
          const canvas = document.createElement('canvas');
          const MAX_SIZE = 240;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_SIZE) {
              height = Math.round((height * MAX_SIZE) / width);
              width = MAX_SIZE;
            }
          } else {
            if (height > MAX_SIZE) {
              width = Math.round((width * MAX_SIZE) / height);
              height = MAX_SIZE;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Canvas rendering context unavailable');

          ctx.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.85);

          await api.users.updateProfile({ avatar_url: dataUrl });
          await refreshUser();
          haptic.success();
          playSuccessSound();
          success('Profile photo updated!', 'Avatar Saved');
          setIsAvatarPickerOpen(false);
        } catch (err: any) {
          toastError(err.message || 'Failed to process photo');
        } finally {
          setIsUploadingPhoto(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  // Set Memoji Preset
  const handleSelectMemoji = async (emoji: string, grad: string[]) => {
    haptic.tap();
    playTapSound();
    setIsUploadingPhoto(true);
    try {
      const uri = createEmojiAvatarUri(emoji, grad);
      await api.users.updateProfile({ avatar_url: uri });
      await refreshUser();
      haptic.success();
      playSuccessSound();
      success('Memoji avatar applied!', 'Style Updated');
      setIsAvatarPickerOpen(false);
    } catch (err: any) {
      toastError(err.message || 'Failed to update avatar');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  // Remove Photo
  const handleRemovePhoto = async () => {
    haptic.button();
    playTapSound();
    setIsUploadingPhoto(true);
    try {
      await api.users.updateProfile({ avatar_url: null });
      await refreshUser();
      info('Profile photo removed', 'Default Monogram Restored');
      setIsAvatarPickerOpen(false);
    } catch (err: any) {
      toastError(err.message || 'Failed to remove photo');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: '640px', paddingBottom: '120px' }}>
      {/* Hidden file input for native camera or gallery selection */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handlePhotoUpload}
        accept="image/*"
        style={{ display: 'none' }}
      />

      {/* 1. Profile Identity Header Card */}
      <div 
        className="apple-card"
        style={{
          padding: '24px 20px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '18px'
        }}
      >
        {/* Avatar Squircle with Camera Badge */}
        <div style={{ position: 'relative' }}>
          <div 
            onClick={() => {
              haptic.tap();
              playTapSound();
              setIsAvatarPickerOpen(prev => !prev);
            }}
            className="ios-tap-target"
            style={{
              width: '74px',
              height: '74px',
              borderRadius: '24px',
              overflow: 'hidden',
              background: isAdmin 
                ? 'linear-gradient(135deg, #0a84ff 0%, #0056b3 100%)' 
                : 'linear-gradient(135deg, #30d158 0%, #1c7c34 100%)',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '2rem',
              fontWeight: 800,
              boxShadow: isAdmin 
                ? '0 10px 25px rgba(10, 132, 255, 0.45)' 
                : '0 10px 25px rgba(48, 209, 88, 0.45)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              cursor: 'pointer'
            }}
            title="Click to customize profile photo"
          >
            {user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user.username}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              user?.username.charAt(0).toUpperCase()
            )}
          </div>

          {/* Camera Edit Badge Button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              haptic.tap();
              playTapSound();
              setIsAvatarPickerOpen(prev => !prev);
            }}
            style={{
              position: 'absolute',
              bottom: '-4px',
              right: '-4px',
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: 'var(--surface-primary)',
              border: '1.5px solid var(--ios-blue)',
              color: 'var(--ios-blue)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              cursor: 'pointer'
            }}
            title="Upload or change avatar"
          >
            <Camera size={14} strokeWidth={2.4} />
          </button>
        </div>

        {/* User Identity Details */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <h1 style={{ 
              fontSize: '1.45rem', 
              fontWeight: 800, 
              color: 'var(--text-primary)', 
              letterSpacing: '-0.02em',
              margin: 0 
            }}>
              {user?.username}
            </h1>
            <span 
              style={{
                fontSize: '0.6875rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                padding: '3px 9px',
                borderRadius: 'var(--radius-pill)',
                background: isAdmin ? 'rgba(10, 132, 255, 0.16)' : 'rgba(48, 209, 88, 0.16)',
                color: isAdmin ? 'var(--ios-blue)' : 'var(--ios-green)',
                border: `0.5px solid ${isAdmin ? 'var(--ios-blue)' : 'var(--ios-green)'}40`
              }}
            >
              {isAdmin ? 'Executive Master' : 'Member Account'}
            </span>
          </div>

          <div style={{ 
            fontSize: '0.8125rem', 
            color: 'var(--text-secondary)', 
            marginTop: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>CK ID:</span>
            <code style={{ 
              fontWeight: 700, 
              color: 'var(--text-primary)',
              background: 'rgba(118, 118, 128, 0.15)',
              padding: '2px 6px',
              borderRadius: '6px',
              fontSize: '0.78rem'
            }}>
              #CK-{String(user?.id || 1).padStart(4, '0')}
            </code>
          </div>

          <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: '4px' }}>
            Authenticated via Secure Edge Session
          </div>
        </div>
      </div>

      {/* Avatar Customization Tray (iOS Expandable Sheet) */}
      {isAvatarPickerOpen && (
        <div
          className="ios-inset-group"
          style={{
            padding: '20px',
            marginBottom: '24px',
            background: 'var(--surface-glass)',
            border: '1px solid rgba(10, 132, 255, 0.35)',
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.25)',
            animation: 'slideDown 0.25s ease-out'
          }}
        >
          <div className="flex-between" style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Camera size={18} color="var(--ios-blue)" />
              <span style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                Customize Profile Avatar
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                haptic.tap();
                playTapSound();
                setIsAvatarPickerOpen(false);
              }}
              style={{
                background: 'rgba(118, 118, 128, 0.18)',
                border: 'none',
                borderRadius: '50%',
                width: '26px',
                height: '26px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-secondary)',
                cursor: 'pointer'
              }}
            >
              <X size={14} />
            </button>
          </div>

          {/* Action Row: Upload from Camera/Gallery & Remove */}
          <div style={{ display: 'grid', gridTemplateColumns: user?.avatar_url ? '1fr 1fr' : '1fr', gap: '10px', marginBottom: '16px' }}>
            <button
              type="button"
              disabled={isUploadingPhoto}
              onClick={() => {
                haptic.tap();
                playTapSound();
                fileInputRef.current?.click();
              }}
              className="ios-tap-target"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '12px 16px',
                borderRadius: 'var(--radius-button)',
                background: 'rgba(10, 132, 255, 0.15)',
                border: '1px solid rgba(10, 132, 255, 0.3)',
                color: 'var(--ios-blue)',
                fontWeight: 700,
                fontSize: '0.875rem',
                cursor: 'pointer'
              }}
            >
              <Upload size={16} />
              <span>{isUploadingPhoto ? 'Processing Photo...' : 'Upload Photo'}</span>
            </button>

            {user?.avatar_url && (
              <button
                type="button"
                disabled={isUploadingPhoto}
                onClick={handleRemovePhoto}
                className="ios-tap-target"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  borderRadius: 'var(--radius-button)',
                  background: 'rgba(255, 69, 58, 0.12)',
                  border: '1px solid rgba(255, 69, 58, 0.25)',
                  color: 'var(--ios-red)',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                <Trash2 size={16} />
                <span>Remove Photo</span>
              </button>
            )}
          </div>

          {/* Presets Header */}
          <div style={{
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--text-tertiary)',
            marginBottom: '10px'
          }}>
            Or Choose an Apple 3D Memoji
          </div>

          {/* Grid of Apple Memoji Presets */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: '10px'
          }}>
            {MEMOJI_PRESETS.map(preset => (
              <button
                key={preset.id}
                type="button"
                onClick={() => handleSelectMemoji(preset.emoji, preset.grad)}
                className="ios-tap-target"
                style={{
                  aspectRatio: '1',
                  borderRadius: '16px',
                  background: `linear-gradient(135deg, ${preset.grad[0]} 0%, ${preset.grad[1]} 100%)`,
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.75rem',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  transition: 'transform var(--duration-fast)'
                }}
                title={preset.label}
              >
                {preset.emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Google Pay Settlement & Payout Account Setting */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ 
          fontSize: '0.72rem', 
          fontWeight: 700, 
          textTransform: 'uppercase', 
          letterSpacing: '0.08em', 
          color: 'var(--text-tertiary)',
          marginLeft: '16px',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <span>Google Pay Settlement Account</span>
          <span style={{
            fontSize: '0.625rem',
            padding: '2px 7px',
            borderRadius: 'var(--radius-pill)',
            background: 'rgba(66, 133, 244, 0.15)',
            color: '#4285f4',
            fontWeight: 800
          }}>
            GPay Exclusive
          </span>
        </div>

        <div className="ios-inset-group" style={{ padding: '18px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '14px' }}>
            <div
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '12px',
                background: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                flexShrink: 0
              }}
            >
              <span style={{ fontSize: '1.05rem', fontWeight: 900, color: '#4285F4' }}>G</span>
              <span style={{ fontSize: '0.9rem', fontWeight: 800, color: '#5f6368' }}>Pay</span>
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                {isAdmin ? 'Admin Receiving Google Pay UPI ID' : 'My Payout Google Pay UPI ID'}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '2px', lineHeight: 1.4 }}>
                {isAdmin 
                  ? 'All members settle their negative balances and debts into this account. Default: chintankheradia123-1@okaxis' 
                  : 'Add your personal Google Pay UPI ID so the Admin can transfer credit payouts directly to your bank account.'}
              </div>
            </div>
          </div>

          <form onSubmit={handleSaveUpi} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                type="text"
                value={upiInput}
                onChange={(e) => setUpiInput(e.target.value)}
                placeholder={isAdmin ? "chintankheradia123-1@okaxis" : "e.g. yourname@okhdfcbank or 9876543210@okaxis"}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '12px',
                  background: 'var(--surface-primary)',
                  border: '1.5px solid var(--border-subtle)',
                  color: 'var(--text-primary)',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  outline: 'none',
                  fontFamily: 'monospace'
                }}
              />
              {upiInput.trim() && (
                <button
                  type="button"
                  onClick={handleCopyUpi}
                  className="ios-tap-target"
                  style={{
                    position: 'absolute',
                    right: '10px',
                    padding: '6px 10px',
                    borderRadius: '8px',
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: 'var(--border-hairline)',
                    color: copiedUpi ? 'var(--ios-green)' : 'var(--text-secondary)',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  title="Copy UPI ID"
                >
                  {copiedUpi ? <Check size={12} strokeWidth={2.6} /> : <Copy size={12} />}
                  <span>{copiedUpi ? 'Copied' : 'Copy'}</span>
                </button>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', flexWrap: 'wrap', gap: '8px' }}>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)' }}>
                {isAdmin ? 'Default: chintankheradia123-1@okaxis' : (user?.upi_id ? '✓ UPI Active on file' : '⚠️ No UPI ID configured yet')}
              </span>

              <button
                type="submit"
                disabled={isSavingUpi}
                className="ios-primary-button ios-tap-target"
                style={{
                  padding: '8px 18px',
                  borderRadius: 'var(--radius-pill)',
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  cursor: isSavingUpi ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {isSavingUpi ? (
                  <span>Saving...</span>
                ) : (
                  <>
                    <Save size={14} />
                    <span>Save Google Pay UPI</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* 2. iOS Settings Inset Group: Tactile & Sound Experience */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ 
          fontSize: '0.72rem', 
          fontWeight: 700, 
          textTransform: 'uppercase', 
          letterSpacing: '0.08em', 
          color: 'var(--text-tertiary)',
          marginLeft: '16px',
          marginBottom: '8px'
        }}>
          Sounds & Haptics
        </div>

        <div className="ios-inset-group">
          {/* Row 1: Touch Sounds */}
          <div 
            className="ios-list-row"
            onClick={handleToggleSound}
            style={{ cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div 
                className="ios-icon-squircle blue"
                style={{ width: '32px', height: '32px', borderRadius: '8px' }}
              >
                {soundActive ? <Volume2 size={17} strokeWidth={2.4} /> : <VolumeX size={17} strokeWidth={2.4} />}
              </div>
              <div>
                <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Touch Sound Effects
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
                  Apple-style synthesized tap and success audio clicks
                </div>
              </div>
            </div>

            {/* Apple Toggle Switch */}
            <div 
              style={{
                width: '50px',
                height: '30px',
                borderRadius: '15px',
                background: soundActive ? 'var(--ios-green)' : 'rgba(120, 120, 128, 0.32)',
                position: 'relative',
                transition: 'background-color 0.25s ease',
                cursor: 'pointer',
                flexShrink: 0
              }}
            >
              <div 
                style={{
                  width: '26px',
                  height: '26px',
                  borderRadius: '50%',
                  background: '#ffffff',
                  position: 'absolute',
                  top: '2px',
                  left: soundActive ? '22px' : '2px',
                  transition: 'left 0.25s cubic-bezier(0.32, 0.72, 0, 1)',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                }}
              />
            </div>
          </div>

          {/* Row 2: Haptic Feedback */}
          <div 
            className="ios-list-row"
            onClick={handleToggleHaptic}
            style={{ cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div 
                className="ios-icon-squircle purple"
                style={{ width: '32px', height: '32px', borderRadius: '8px' }}
              >
                <Vibrate size={17} strokeWidth={2.4} />
              </div>
              <div>
                <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Haptic Feedback
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
                  Tactile device vibration on taps and actions
                </div>
              </div>
            </div>

            {/* Apple Toggle Switch */}
            <div 
              style={{
                width: '50px',
                height: '30px',
                borderRadius: '15px',
                background: hapticActive ? 'var(--ios-green)' : 'rgba(120, 120, 128, 0.32)',
                position: 'relative',
                transition: 'background-color 0.25s ease',
                cursor: 'pointer',
                flexShrink: 0
              }}
            >
              <div 
                style={{
                  width: '26px',
                  height: '26px',
                  borderRadius: '50%',
                  background: '#ffffff',
                  position: 'absolute',
                  top: '2px',
                  left: hapticActive ? '22px' : '2px',
                  transition: 'left 0.25s cubic-bezier(0.32, 0.72, 0, 1)',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 3. iOS Settings Inset Group: Appearance */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ 
          fontSize: '0.72rem', 
          fontWeight: 700, 
          textTransform: 'uppercase', 
          letterSpacing: '0.08em', 
          color: 'var(--text-tertiary)',
          marginLeft: '16px',
          marginBottom: '8px'
        }}>
          Appearance
        </div>

        <div className="ios-inset-group">
          <div className="ios-list-row">
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div 
                className="ios-icon-squircle orange"
                style={{ width: '32px', height: '32px', borderRadius: '8px' }}
              >
                {theme === 'dark' ? <Moon size={17} strokeWidth={2.4} /> : <Sun size={17} strokeWidth={2.4} />}
              </div>
              <div>
                <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Display Theme
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
                  {theme === 'dark' ? 'Titanium Space Black (OLED)' : 'Ceramic Crystal White'}
                </div>
              </div>
            </div>

            {/* Segmented Theme Picker */}
            <div className="ios-segmented-control" style={{ padding: '3px' }}>
              <button
                type="button"
                onClick={() => {
                  haptic.tap();
                  playTapSound();
                  setTheme('dark');
                }}
                className={`ios-segment-button ${theme === 'dark' ? 'active' : ''}`}
                style={{ padding: '4px 10px', fontSize: '0.75rem', gap: '4px', display: 'flex', alignItems: 'center' }}
              >
                <Moon size={12} />
                <span>Dark</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  haptic.tap();
                  playTapSound();
                  setTheme('light');
                }}
                className={`ios-segment-button ${theme === 'light' ? 'active' : ''}`}
                style={{ padding: '4px 10px', fontSize: '0.75rem', gap: '4px', display: 'flex', alignItems: 'center' }}
              >
                <Sun size={12} />
                <span>Light</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* iOS Settings Inset Group: App Icon & Brand Logo */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ 
          fontSize: '0.72rem', 
          fontWeight: 700, 
          textTransform: 'uppercase', 
          letterSpacing: '0.08em', 
          color: 'var(--text-tertiary)',
          marginLeft: '16px',
          marginBottom: '8px'
        }}>
          App Icon & Logo Style
        </div>

        <div className="ios-inset-group" style={{ padding: '16px' }}>
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '14px' }}>
            Customize your 3D brand logo icon variant across CKAudit headers, sign-in, and splash screen:
          </div>

          {/* Cards for Logo Selection */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            {/* 1. Auto (Sync with Theme) */}
            <div
              onClick={() => {
                haptic.selection();
                playTapSound();
                setLogoStyle('auto');
                success('App icon set to sync with display theme', 'Logo Updated');
              }}
              style={{
                borderRadius: '18px',
                padding: '12px',
                background: logoStyle === 'auto' ? 'rgba(10, 132, 255, 0.12)' : 'var(--surface-primary)',
                border: logoStyle === 'auto' ? '1.5px solid var(--ios-blue)' : '1px solid var(--border-hairline)',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s ease'
              }}
              className="ios-tap-target"
            >
              <div style={{ position: 'relative', width: '56px', height: '56px', margin: '0 auto 8px' }}>
                <img
                  src={theme === 'dark' ? '/logos/logo-dark.png' : '/logos/logo-light.png'}
                  alt="Auto Logo"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '14px' }}
                />
                {logoStyle === 'auto' && (
                  <div style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    background: 'var(--ios-blue)',
                    color: '#fff',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                  }}>
                    <CheckCircle2 size={12} strokeWidth={3} />
                  </div>
                )}
              </div>
              <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                Auto (Adaptive)
              </div>
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                Matches Light/Dark
              </div>
            </div>

            {/* 2. OLED Neon Blue (Dark) */}
            <div
              onClick={() => {
                haptic.selection();
                playTapSound();
                setLogoStyle('dark');
                success('OLED Neon Dark logo activated', 'Logo Updated');
              }}
              style={{
                borderRadius: '18px',
                padding: '12px',
                background: logoStyle === 'dark' ? 'rgba(10, 132, 255, 0.12)' : 'var(--surface-primary)',
                border: logoStyle === 'dark' ? '1.5px solid var(--ios-blue)' : '1px solid var(--border-hairline)',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s ease'
              }}
              className="ios-tap-target"
            >
              <div style={{ position: 'relative', width: '56px', height: '56px', margin: '0 auto 8px' }}>
                <img
                  src="/logos/logo-dark.png"
                  alt="Dark Neon Logo"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '14px' }}
                />
                {logoStyle === 'dark' && (
                  <div style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    background: 'var(--ios-blue)',
                    color: '#fff',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                  }}>
                    <CheckCircle2 size={12} strokeWidth={3} />
                  </div>
                )}
              </div>
              <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                OLED Neon
              </div>
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                Dark Glassmorphic
              </div>
            </div>

            {/* 3. Titanium Clean (Light) */}
            <div
              onClick={() => {
                haptic.selection();
                playTapSound();
                setLogoStyle('light');
                success('Titanium Clean Light logo activated', 'Logo Updated');
              }}
              style={{
                borderRadius: '18px',
                padding: '12px',
                background: logoStyle === 'light' ? 'rgba(10, 132, 255, 0.12)' : 'var(--surface-primary)',
                border: logoStyle === 'light' ? '1.5px solid var(--ios-blue)' : '1px solid var(--border-hairline)',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s ease'
              }}
              className="ios-tap-target"
            >
              <div style={{ position: 'relative', width: '56px', height: '56px', margin: '0 auto 8px' }}>
                <img
                  src="/logos/logo-light.png"
                  alt="Light Neumorphic Logo"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '14px' }}
                />
                {logoStyle === 'light' && (
                  <div style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    background: 'var(--ios-blue)',
                    color: '#fff',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                  }}>
                    <CheckCircle2 size={12} strokeWidth={3} />
                  </div>
                )}
              </div>
              <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                Titanium Clean
              </div>
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                Light Neumorphic
              </div>
            </div>
          </div>

          {/* Action Row: Preview Opening Launch Animation */}
          <button
            type="button"
            onClick={() => {
              haptic.tap();
              playTapSound();
              window.dispatchEvent(new CustomEvent('ckaudit:preview_splash'));
            }}
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: '14px',
              background: 'var(--surface-primary)',
              border: '1px solid var(--border-highlight)',
              color: 'var(--ios-blue)',
              fontSize: '0.875rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            className="ios-tap-target"
          >
            <Sparkles size={16} />
            <span>Play App Launch Animation</span>
          </button>
        </div>
      </div>

      {/* 4. iOS Settings Inset Group: Web Push Notifications */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ 
          fontSize: '0.72rem', 
          fontWeight: 700, 
          textTransform: 'uppercase', 
          letterSpacing: '0.08em', 
          color: 'var(--text-tertiary)',
          marginLeft: '16px',
          marginBottom: '8px'
        }}>
          Notifications
        </div>

        <div className="ios-inset-group">
          <div 
            className="ios-list-row"
            onClick={async () => {
              haptic.tap();
              playTapSound();
              if (permission !== 'granted') {
                await requestPermission();
              } else {
                info('Web push notifications are already enabled on this device', 'Subscribed');
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div 
                className="ios-icon-squircle red"
                style={{ width: '32px', height: '32px', borderRadius: '8px' }}
              >
                <Bell size={17} strokeWidth={2.4} />
              </div>
              <div>
                <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Instant Push Notifications
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
                  Receive live alerts on transactions and approvals
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{
                fontSize: '0.72rem',
                fontWeight: 700,
                color: permission === 'granted' ? 'var(--ios-green)' : 'var(--text-tertiary)',
                textTransform: 'capitalize'
              }}>
                {permission === 'granted' ? 'Active' : permission === 'denied' ? 'Blocked' : 'Enable'}
              </span>
              <ChevronRight size={16} color="var(--text-tertiary)" />
            </div>
          </div>
        </div>
      </div>

      {/* 5. iOS Settings Inset Group: Statements & Accounting */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ 
          fontSize: '0.72rem', 
          fontWeight: 700, 
          textTransform: 'uppercase', 
          letterSpacing: '0.08em', 
          color: 'var(--text-tertiary)',
          marginLeft: '16px',
          marginBottom: '8px'
        }}>
          Reports & Statements
        </div>

        <div className="ios-inset-group">
          {/* Export CSV */}
          <div 
            className="ios-list-row"
            onClick={handleExportCSV}
            style={{ cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div 
                className="ios-icon-squircle green"
                style={{ width: '32px', height: '32px', borderRadius: '8px' }}
              >
                <FileSpreadsheet size={17} strokeWidth={2.4} />
              </div>
              <div>
                <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Export Statement (CSV)
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
                  Excel & Apple Numbers compatible UTF-8 spreadsheet
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {isExporting ? (
                <span style={{ fontSize: '0.75rem', color: 'var(--ios-blue)', fontWeight: 600 }}>Exporting...</span>
              ) : (
                <ChevronRight size={16} color="var(--text-tertiary)" />
              )}
            </div>
          </div>

          {/* Print PDF */}
          <div 
            className="ios-list-row"
            onClick={handlePrint}
            style={{ cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div 
                className="ios-icon-squircle indigo"
                style={{ width: '32px', height: '32px', borderRadius: '8px' }}
              >
                <CreditCard size={17} strokeWidth={2.4} />
              </div>
              <div>
                <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  CKAudit Titanium Statement & PDF
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
                  Executive card statement & certified multi-page PDF
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {isLoadingStatement ? (
                <span style={{ fontSize: '0.75rem', color: 'var(--ios-blue)', fontWeight: 600 }}>Loading...</span>
              ) : (
                <ChevronRight size={16} color="var(--text-tertiary)" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Security & Password Section */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ 
          fontSize: '0.72rem', 
          fontWeight: 700, 
          textTransform: 'uppercase', 
          letterSpacing: '0.08em', 
          color: 'var(--text-tertiary)',
          marginLeft: '16px',
          marginBottom: '8px'
        }}>
          Security & Password
        </div>

        <div className="ios-inset-group">
          <div 
            className="ios-list-row"
            onClick={() => {
              haptic.tap();
              playTapSound();
              setIsPasswordModalOpen(true);
            }}
            style={{ cursor: 'pointer' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div 
                className="ios-icon-squircle blue"
                style={{ width: '32px', height: '32px', borderRadius: '8px' }}
              >
                <KeyRound size={17} strokeWidth={2.4} />
              </div>
              <div>
                <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Change Login Password
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
                  Update your authentication password credentials
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '0.78rem', color: 'var(--ios-blue)', fontWeight: 600 }}>Update</span>
              <ChevronRight size={16} color="var(--text-tertiary)" />
            </div>
          </div>
        </div>
      </div>

      {/* 6. Admin Management Shortcuts (if Admin) */}
      {isAdmin && (
        <div style={{ marginBottom: '24px' }}>
          <div style={{ 
            fontSize: '0.72rem', 
            fontWeight: 700, 
            textTransform: 'uppercase', 
            letterSpacing: '0.08em', 
            color: 'var(--text-tertiary)',
            marginLeft: '16px',
            marginBottom: '8px'
          }}>
            Master Administration
          </div>

          <div className="ios-inset-group">
            <div 
              className="ios-list-row"
              onClick={() => {
                haptic.tap();
                playTapSound();
                onNavigateTab('users');
              }}
              style={{ cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div 
                  className="ios-icon-squircle indigo"
                  style={{ width: '32px', height: '32px', borderRadius: '8px' }}
                >
                  <Users size={17} strokeWidth={2.4} />
                </div>
                <div>
                  <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    Member Management
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
                    Manage 17 ledger accounts, balances and credentials
                  </div>
                </div>
              </div>

              <ChevronRight size={16} color="var(--text-tertiary)" />
            </div>

            <div 
              className="ios-list-row"
              onClick={() => {
                haptic.tap();
                playTapSound();
                onNavigateTab('categories');
              }}
              style={{ cursor: 'pointer' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div 
                  className="ios-icon-squircle orange"
                  style={{ width: '32px', height: '32px', borderRadius: '8px' }}
                >
                  <Tag size={17} strokeWidth={2.4} />
                </div>
                <div>
                  <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    Category Master
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
                    Configure expense categories, color tags, and icons
                  </div>
                </div>
              </div>

              <ChevronRight size={16} color="var(--text-tertiary)" />
            </div>
          </div>
        </div>
      )}

      {/* 7. Destructive Sign Out */}
      <div style={{ marginTop: '32px' }}>
        <button
          type="button"
          onClick={() => {
            haptic.button();
            playTapSound();
            logout();
          }}
          className="ios-tap-target"
          style={{
            width: '100%',
            padding: '16px',
            borderRadius: 'var(--radius-lg)',
            background: 'rgba(255, 69, 58, 0.12)',
            border: '0.5px solid rgba(255, 69, 58, 0.25)',
            color: 'var(--ios-red)',
            fontSize: '0.9375rem',
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all var(--duration-fast)'
          }}
        >
          <LogOut size={18} strokeWidth={2.4} />
          <span>Sign Out with CK ID</span>
        </button>

        <div style={{ 
          textAlign: 'center', 
          fontSize: '0.6875rem', 
          color: 'var(--text-tertiary)', 
          marginTop: '14px' 
        }}>
          CKAudit • Secure Ledger Architecture
        </div>
      </div>

      {/* Printable Executive Ledger Statement Modal */}
      <AppleCardStatementModal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        transactions={statementTransactions}
        summary={statementSummary}
        currentUser={user}
      />

      {/* Change Password Modal (Self-service) */}
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        isSelf={true}
      />
    </div>
  );
};
